import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * A wrapping component for delaying the render of one component by substituting it for another until intersected.
 * Inspired by react-render-if-visible, with a few differences:
 * 	* simplified way to provide options to intersectionObserver
 * 	* way to trigger a callback once an entry has intersected
 * 	* only works with one child, `children` should never contain more than one element, it won't work
 * 	* does not re-substitute once child has been fully rendered.
 * 	* no extra wrapping dom elements!
 * https://github.com/NightCafeStudio/react-render-if-visible
 *
 * This component wrapper provides what I am calling 'substitution', it is not really virtualization, in my opinion.
 * The difference is that a virtualized list uses height to calculate the space above and below the virtualized list.
 * In "substitution", the child and the substitute components are rendered together in a list that is not 'virtualized',
 * and without height calculation. I don't know if that distinction matters when we call things 'virtualized' or not.
 *
 * Currently, the substitute and child components should be close to the same height.
 *
 * Were we ever to need to implement infinite scrolling,
 * this would need to be readdressed as the number of elements in the list would become a concern.
 *
 * The substitute and child components MUST be created with forwardRef, preferably named.
 * https://reactjs.org/docs/forwarding-refs.html
 *
 * This component is entirely abstracted from ws-inv-listing (please keep it that way)
 * It could be moved into a module if we required this functionality elsewhere.
 *
 * @param observerOptions properties of intersection observer. See
 * 	https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver#properties
 * @param substitute placeholder for child component
 * @param onIntersectCallback callback that fires when an intersection observe entry is intersecting
 * 	https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserverEntry/isIntersecting
 * @param children component that fully renders after a substitute is intersected. Cannot have multiple children, only 1
 * @returns {React.CElement<unknown, React.Component<unknown, React.ComponentState>>}
 * @constructor
 */
const SubstituteWrapper = ({
	observerOptions,
	substitute,
	onIntersectCallback,
	children
}) => {
	const [hasIntersected, setHasIntersected] = useState(false);
	const substituteRef = useRef(null);
	const canUseIntersectionObserver =
		typeof IntersectionObserver !== 'undefined';

	useEffect(() => {
		if (
			!hasIntersected &&
			substituteRef.current &&
			canUseIntersectionObserver
		) {
			const node = substituteRef.current;
			const observer = new IntersectionObserver((entries) => {
				setHasIntersected(entries[0].isIntersecting);
				// do not continue substituting once it has intersected the first time
				// without this, the children un-render and go back to substitute components
				// I don't feel it is necessary to un-replace a fully rendered child currently
				if (entries[0].isIntersecting) {
					observer.unobserve(node);
					observer.disconnect();
				}
				onIntersectCallback(entries);
			}, observerOptions);
			if (!hasIntersected) {
				observer.observe(node);
			}

			return () => {
				if (node) {
					observer.unobserve(node);
				}
				observer.disconnect();
			};
		}
		return () => {};
	}, [hasIntersected, onIntersectCallback, canUseIntersectionObserver]); // eslint-disable-line react-hooks/exhaustive-deps
	// ignoring exhaustive-deps here because the observerOptions are an object that would cause this
	// useEffect to trigger every time the parent component rerenders. observerOptions should never change.
	return hasIntersected || !canUseIntersectionObserver || !substitute
		? React.cloneElement(children, { ref: substituteRef })
		: React.cloneElement(substitute, { ref: substituteRef });
};

SubstituteWrapper.propTypes = {
	observerOptions: PropTypes.shape({}),
	substitute: PropTypes.shape({}),
	onIntersectCallback: PropTypes.func,
	children: PropTypes.shape({}).isRequired
};

export default SubstituteWrapper;
